import { useEffect, useRef, useState, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * How long an Undo notice stays on screen: 8 seconds, the single constant for
 * every Undo in the Toolbox (epic PI-SPEC-SW-E294, feature PI-SPEC-SW-1508).
 *
 * The ER says "about 8 seconds" and frames F3 and F4 show "8 s"; the F3 note's
 * "about 10 seconds" is superseded. Deliberately separate from the grooming
 * toasts' GROOMING_TOAST_MS (3200 ms): the Roles page renders outside the
 * grooming layout and its toast store.
 */
export const UNDO_NOTICE_MS = 8000;

/** Seconds left on the countdown, rounded up so it reads "8 s" at the start and "1 s" at the end. */
export function undoSecondsLeft(remainingMs: number): number {
  return Math.max(0, Math.ceil(remainingMs / 1000));
}

export interface UndoNoticeProps {
  /** Lucide icon for what was removed (F3: Archive, F4: UserRoundMinus). Optional. */
  icon?: LucideIcon;
  /** What happened and what it means, e.g. `Removed <b>"Rain delay"</b>. Briefs written so far keep the name.` */
  message: ReactNode;
  /** Called once when the person presses Undo before the notice expires. */
  onUndo: () => void;
  /** Called once, exactly {@link UNDO_NOTICE_MS} after the notice appears, unless Undo was pressed first. */
  onExpire: () => void;
  /** True while the Undo request is in flight: the button is disabled and reads "Undoing…". */
  pending?: boolean;
  className?: string;
}

/**
 * The one Undo notice shared by Project → Product areas (frame F3) and Roles
 * (frame F4) — design classes `.undo`, `.undob`, `.ucount` in Claude Design
 * "PM Packages B E F Mockups". A removal acts at once and this bar offers to
 * take it back for {@link UNDO_NOTICE_MS}, instead of a confirm dialog.
 *
 * The timer starts on mount. Render a new notice with a new React `key` (the
 * notice id from `useUndoNotice`) so a replacement restarts the countdown.
 * Callback props may change between renders without restarting the timer.
 */
export function UndoNotice({ icon: Icon, message, onUndo, onExpire, pending = false, className }: UndoNoticeProps) {
  const [remainingMs, setRemainingMs] = useState(UNDO_NOTICE_MS);
  const [undone, setUndone] = useState(false);
  const settledRef = useRef(false);
  const onExpireRef = useRef(onExpire);
  const onUndoRef = useRef(onUndo);

  useEffect(() => {
    onExpireRef.current = onExpire;
    onUndoRef.current = onUndo;
  }, [onExpire, onUndo]);

  useEffect(() => {
    const startedAt = Date.now();
    const tick = window.setInterval(() => {
      setRemainingMs(Math.max(0, UNDO_NOTICE_MS - (Date.now() - startedAt)));
    }, 1000);
    const expiry = window.setTimeout(() => {
      window.clearInterval(tick);
      if (settledRef.current) return;
      settledRef.current = true;
      onExpireRef.current();
    }, UNDO_NOTICE_MS);
    return () => {
      window.clearInterval(tick);
      window.clearTimeout(expiry);
    };
  }, []);

  const handleUndo = () => {
    if (settledRef.current || pending) return;
    settledRef.current = true;
    setUndone(true);
    onUndoRef.current();
  };

  const busy = pending || undone;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="undo-notice"
      className={cn(
        "fixed bottom-[22px] left-1/2 z-50 flex min-w-[470px] -translate-x-[38%] items-center gap-3",
        "rounded-[10px] border border-[#3F3F46] bg-[#27272A] px-3.5 py-2.5 text-[#FAFAFA]",
        "shadow-[0_18px_40px_rgba(0,0,0,0.55)]",
        TOOLBOX_TEXT.content,
        className
      )}
    >
      {Icon ? <Icon className="h-4 w-4 flex-none text-[#A1A1AA]" aria-hidden="true" /> : null}
      <span className="min-w-0 flex-1 leading-snug">{message}</span>
      <button
        type="button"
        onClick={handleUndo}
        disabled={busy}
        aria-busy={pending || undefined}
        data-testid="undo-notice-undo"
        className={cn(
          "flex-none rounded px-1.5 py-0.5 font-semibold text-[#6CC4FF]",
          "hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6CC4FF]",
          "disabled:cursor-default disabled:no-underline disabled:opacity-60"
        )}
      >
        {busy ? "Undoing…" : "Undo"}
      </button>
      {busy ? null : (
        <span className={cn("flex-none tabular-nums text-[#A1A1AA]", TOOLBOX_TEXT.meta)} data-testid="undo-notice-countdown">
          {undoSecondsLeft(remainingMs)} s
        </span>
      )}
    </div>
  );
}
